import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft, Info, AlertTriangle, AtSign, Loader2, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Login notification state (shared with login page)
  const [siteNotification, setSiteNotification] = useState(null);
  
  // Invite code from URL
  const [inviteCode, setInviteCode] = useState(null);
  const [inviter, setInviter] = useState(null);
  const [inviteChecking, setInviteChecking] = useState(false);
  
  // Check for invite code in URL and validate it
  useEffect(() => {
    const code = searchParams.get('ref') || searchParams.get('invite');
    if (code) {
      setInviteCode(code);
      validateInviteCode(code);
    }
  }, [searchParams]);
  
  const validateInviteCode = async (code) => {
    setInviteChecking(true);
    try {
      const response = await fetch(`${API}/public/invite/validate/${encodeURIComponent(code)}`);
      const data = await response.json();
      if (data.valid) {
        setInviter(data.inviter);
      } else {
        setInviter(null);
      }
    } catch (err) {
      console.error("Failed to validate invite code:", err);
      setInviter(null);
    } finally {
      setInviteChecking(false);
    }
  };
  
  // Fetch site notification on mount
  useEffect(() => {
    const fetchNotification = async () => {
      try {
        const response = await fetch(`${API}/public/site-settings/login-notification`);
        const data = await response.json();
        if (data.enabled && data.message) {
          setSiteNotification(data);
        }
      } catch (err) {
        console.error("Failed to fetch site notification:", err);
      }
    };
    fetchNotification();
  }, []);
  
  // Check localStorage for existing user session and redirect if logged in
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        // User data exists, redirect to profile
        navigate("/app/profile", { replace: true });
      } catch (e) {
        // Invalid stored data, clear it
        localStorage.removeItem("user");
      }
    }
  }, [navigate]);
  
  // Get return URL from state (passed from creator profile page or login page) or sessionStorage
  const returnUrl = location.state?.from || sessionStorage.getItem('returnUrl') || null;
  
  // Store return URL in sessionStorage for persistence
  if (location.state?.from) {
    sessionStorage.setItem('returnUrl', location.state.from);
  }
  
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Username availability state
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, error: null });
  
  // Debounced username check
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false, available: null, error: null });
      return;
    }
    
    setUsernameStatus({ checking: true, available: null, error: null });
    
    try {
      const response = await fetch(`${API}/public/auth/check-username/${encodeURIComponent(username)}`);
      const data = await response.json();
      
      if (data.error) {
        setUsernameStatus({ checking: false, available: false, error: data.error });
      } else {
        setUsernameStatus({ checking: false, available: data.available, error: null });
      }
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, error: "Failed to check availability" });
    }
  }, []);
  
  // Debounce username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsernameAvailability(formData.username);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.username, checkUsernameAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For username, only allow valid characters and lowercase
    if (name === "username") {
      const sanitized = value.toLowerCase().replace(/[^a-z0-9_.]/g, "");
      setFormData({ ...formData, [name]: sanitized });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError("");
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError("Username is required");
      return false;
    }
    if (formData.username.length < 3) {
      setError("Username must be at least 3 characters");
      return false;
    }
    if (formData.username.length > 20) {
      setError("Username must be at most 20 characters");
      return false;
    }
    if (usernameStatus.available === false) {
      setError(usernameStatus.error || "Username is not available");
      return false;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return false;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API}/public/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          username: formData.username,
          invite_code: inviteCode || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[var(--card)] rounded-2xl p-8 max-w-md w-full text-center border border-[var(--border)] shadow-xl"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--foreground)] mb-2">Check Your Email</h2>
          <p className="text-[var(--muted-foreground)] mb-6">
            We've sent a verification link to <span className="text-[var(--primary)] font-medium">{formData.email}</span>. 
            Please check your inbox and click the link to verify your account.
          </p>
          <Link
            to="/login"
            state={returnUrl ? { from: returnUrl } : undefined}
            className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[var(--card)] rounded-2xl p-8 max-w-md w-full border border-[var(--border)] shadow-xl"
      >
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <a href="/" className="text-2xl font-bold inline-block" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
            <span className="text-[var(--foreground)]">HotPeek</span>
          </a>
          <p className="text-[var(--muted-foreground)] mt-2">Create your account</p>
        </div>

        {/* Site Notification */}
        {siteNotification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-lg p-4 mb-6 flex items-start gap-3 ${
              siteNotification.type === "info" 
                ? "bg-cyan-500/10 border border-cyan-500/30"
                : siteNotification.type === "warning"
                  ? "bg-amber-500/10 border border-amber-500/30"
                  : "bg-green-500/10 border border-green-500/30"
            }`}
            data-testid="register-notification"
          >
            {siteNotification.type === "info" ? (
              <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 text-cyan-400`} />
            ) : siteNotification.type === "warning" ? (
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 text-amber-400`} />
            ) : (
              <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 text-green-400`} />
            )}
            <p className={`text-sm ${
              siteNotification.type === "info" 
                ? "text-cyan-400"
                : siteNotification.type === "warning"
                  ? "text-amber-400"
                  : "text-green-400"
            }`}>
              {siteNotification.message}
            </p>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 border border-red-300 rounded-lg p-3 mb-6 flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-600 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Invite Banner */}
        {inviter && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center gap-3"
            data-testid="invite-banner"
          >
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              {inviter.profile_image ? (
                <img 
                  src={inviter.profile_image.startsWith("data:") ? inviter.profile_image : `data:image/jpeg;base64,${inviter.profile_image}`}
                  alt={inviter.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserPlus className="w-5 h-5 text-green-400" />
              )}
            </div>
            <div>
              <p className="text-green-400 text-sm font-medium">You've been invited!</p>
              <p className="text-green-300 text-xs">
                Invited by <span className="font-semibold">{inviter.name}</span>
              </p>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Username */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Username</label>
            <div className="relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Choose a username"
                maxLength={20}
                className={`w-full bg-[var(--background)] border rounded-lg py-3 pl-10 pr-12 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none transition-colors ${
                  usernameStatus.available === true
                    ? "border-green-500 focus:border-green-500"
                    : usernameStatus.available === false
                      ? "border-red-500 focus:border-red-500"
                      : "border-[var(--border)] focus:border-[var(--primary)]"
                }`}
                data-testid="register-username-input"
              />
              {/* Status indicator */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {usernameStatus.checking ? (
                  <Loader2 className="w-5 h-5 text-[var(--muted-foreground)] animate-spin" />
                ) : usernameStatus.available === true ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : usernameStatus.available === false ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : null}
              </div>
            </div>
            {/* Username feedback */}
            {formData.username && (
              <p className={`text-xs mt-1.5 ${
                usernameStatus.available === true
                  ? "text-green-500"
                  : usernameStatus.available === false
                    ? "text-red-500"
                    : "text-[var(--muted-foreground)]"
              }`}>
                {usernameStatus.checking
                  ? "Checking availability..."
                  : usernameStatus.error
                    ? usernameStatus.error
                    : usernameStatus.available === true
                      ? `@${formData.username} is available!`
                      : usernameStatus.available === false
                        ? "Username is already taken"
                        : "3-20 characters, letters, numbers, _ and . only"}
              </p>
            )}
            {!formData.username && (
              <p className="text-xs mt-1.5 text-[var(--muted-foreground)]">
                3-20 characters, letters, numbers, _ and . only
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className="w-full bg-[var(--background)] border border-[var(--border)] rounded-lg py-3 pl-10 pr-4 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                data-testid="register-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Min. 8 characters"
                className="w-full bg-[var(--background)] border border-[var(--border)] rounded-lg py-3 pl-10 pr-12 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-[var(--foreground)] text-sm mb-2">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted-foreground)]" />
              <input
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="w-full bg-[var(--background)] border border-[var(--border)] rounded-lg py-3 pl-10 pr-4 text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                data-testid="register-confirm-password-input"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--primary)] text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating Account...
              </span>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        {/* Login Link */}
        <p className="text-center text-[var(--muted-foreground)] mt-6">
          Already have an account?{" "}
          <Link 
            to="/login" 
            state={returnUrl ? { from: returnUrl } : undefined}
            className="text-[var(--primary)] hover:underline transition-colors"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
